import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Coffee,
  Building2,
  Footprints,
  Sparkles,
  Hotel,
  Utensils,
  Beer,
  Landmark,
  TreePine,
  Ship,
  ShoppingBag,
  Plane,
  Camera,
  Waves,
  Crown,
  ShieldCheck,
  X,
  Map,
  Flame,
  Gift,
  Bus,
  BookOpen,
  IceCream,
} from 'lucide-react';
import ItineraryMap, { buildMapLocations } from './ItineraryMap';
import { londonCoordinates, londonCenter } from './coordinates/london';
import FuellingView from './events/FuellingView';
import ShoppingView from './events/ShoppingView';
import RaceWeekView from './events/RaceWeekView';
import {
  londonRace,
  londonFuelling,
  londonShopping,
  londonRaceWeek,
  LONDON_LABEL,
  LONDON_GUN,
} from './events/london';

type LondonSection = 'itinerary' | 'fuelling' | 'shopping' | 'free-stuff';

const sectionToHash = (section: LondonSection): string => (section === 'itinerary' ? '#london' : `#london/${section}`);

const hashToSection = (hash: string): LondonSection => {
  if (hash === '#london/fuelling') return 'fuelling';
  if (hash === '#london/shopping') return 'shopping';
  if (hash === '#london/free-stuff') return 'free-stuff';
  return 'itinerary';
};

const subNav: { id: LondonSection; label: string; sub: string; icon: React.ReactNode }[] = [
  { id: 'itinerary', label: 'Itinerary', sub: '8 days', icon: <Map size={18} /> },
  { id: 'fuelling', label: 'Fuelling', sub: 'Race week', icon: <Flame size={18} /> },
  { id: 'shopping', label: 'Shopping', sub: 'Groceries', icon: <ShoppingBag size={18} /> },
  { id: 'free-stuff', label: 'Free Stuff', sub: 'Medal perks', icon: <Gift size={18} /> },
];

interface CoeliacRef {
  title: string;
  url: string;
  summary: string;
}

interface CoeliacUKAccreditation {
  since?: number;
  url: string;
}

interface Activity {
  icon: React.ReactNode;
  title: string;
  description: string;
  time?: string;
  image?: string;
  location?: string;
  muted?: boolean;
  coeliacRefs?: CoeliacRef[];
  coeliacSummary?: string;
  coeliacUK?: CoeliacUKAccreditation;
}

interface Day {
  date: string;
  dayNumber: number;
  title: string;
  subtitle: string;
  theme: 'london-arrival' | 'london-explore' | 'london-taper' | 'london-race' | 'london-recovery';
  activities: Activity[];
  highlight?: boolean;
}

const londonItinerary: Day[] = [
  {
    date: 'Wednesday, April 22',
    dayNumber: 1,
    title: 'Arrival, Southbank & Covent Garden',
    subtitle: 'Check in, walk the Thames & first evening in London',
    theme: 'london-arrival',
    activities: [
      {
        icon: <Hotel size={24} />,
        title: 'NYX Hotel London Holborn',
        description:
          'Check in to the NYX on Southampton Row — a lifestyle hotel in the heart of Holborn. Your base for the first five nights, including race day. A 5-minute walk to the British Museum, 10 minutes to Covent Garden, and well connected by Tube (Holborn station: Central and Piccadilly Lines). Piccadilly Line runs direct to Heathrow. Check-in is 3:00 PM — the flight lands at 12:25, so timing should work after immigration and transfer.',
        location: 'Southampton Row, Holborn',
        time: 'Afternoon',
        image: '/images/london/nyx-hotel.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'South Bank Walk',
        description:
          "A 2km stroll east along the Thames from Westminster Bridge past the London Eye, Southbank Centre, National Theatre, and Shakespeare's Globe. Street performers, book stalls, and river views the whole way. Flat and car-free — an easy way to loosen up after the flight.",
        location: 'Southbank Centre, Belvedere Road',
        time: '3:30 PM',
        image: '/images/london/south-bank.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Covent Garden Piazza',
        description:
          "Loop back from the river to London's famous market square. The original fruit and vegetable market dates to 1654; today it's home to independent boutiques, street performers, and the glass-roofed Apple Market. The Royal Opera House and London Transport Museum border the piazza. A 10-minute walk from the hotel.",
        location: 'Covent Garden Piazza',
        time: '5:00 PM',
        image: '/images/london/covent-garden.jpg',
      },
    ],
  },
  {
    date: 'Thursday, April 23',
    dayNumber: 2,
    title: 'Victoria Park, Museums, Tower Bridge & Marathon Expo',
    subtitle: 'East London park, a cathedral of science, Borough lunch, Tower Bridge & race pack at ExCeL',
    theme: 'london-explore',
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'Victoria Park Early Morning Run',
        description:
          'Up early and on the Tube from Holborn before 6am — Piccadilly Line to Mile End, then a short walk to the park. 6.4km around the perimeter of the oldest public park in London, finishing near the Old Ford end. Vicky Park at dawn: almost nobody around, just joggers and dog walkers on the long tree-lined avenues. Tube back to the hotel by 8:30am to shower and fuel up before the day.',
        location: 'Victoria Park, Bow',
        time: '6:30 AM',
        image: '/images/london/victoria-park.jpg',
      },
      {
        icon: <Building2 size={24} />,
        title: 'Natural History Museum',
        description:
          'Free entry. A Romanesque building with the blue whale skeleton hanging in Hintze Hall. District Line from Mile End straight through to South Kensington. Head straight to Hintze Hall, then the dinosaur gallery and the mineralogy vault — entering via the Exhibition Road side door skips the main-entrance queue.',
        location: 'Cromwell Road, South Kensington',
        time: '12:00 PM',
        image: '/images/london/natural-history.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Borough Market Lunch',
        description:
          "London's oldest food market, trading since 1014. Over 100 stalls of artisan food from around the world. GF highlights: Khanom Krok (almost entirely GF Thai street food — rice-based, a coeliac favourite), Free From Bakehouse (dedicated GF bakery, independently tests facility for gluten every 6 months), and Gujarati Rasoi (vegetarian/vegan Gujarati street food, mostly GF — check individual dishes). Open Tue–Sat. District Line from South Kensington → Monument, then 5 min walk.",
        location: 'Borough Market, Southwark',
        time: '2:00 PM',
        image: '/images/london/borough-market.jpg',
        coeliacSummary:
          'Multiple dedicated GF stalls confirmed by coeliac bloggers. Free From Bakehouse independently tests for gluten every 6 months. Khanom Krok and Gujarati Rasoi are coeliac favourites.',
        coeliacRefs: [
          {
            title: 'Free From Bakehouse',
            url: 'https://boroughmarket.org.uk/traders/the-free-from-bakehouse/',
            summary: 'Official listing. Dedicated GF bakery, tests facility for gluten every 6 months.',
          },
          {
            title: 'Gujarati Rasoi',
            url: 'https://boroughmarket.org.uk/traders/gujarati-rasoi/',
            summary: 'Official listing. Vegetarian/vegan Gujarati street food, mostly naturally GF.',
          },
          {
            title: 'Gluten Free Alchemist',
            url: 'https://www.glutenfreealchemist.com/gluten-free-borough-market-london-guide/',
            summary: 'Comprehensive GF stall-by-stall guide by a coeliac blogger.',
          },
          {
            title: 'How To Coeliac',
            url: 'https://www.how-to-coeliac.com/gluten-free-london-markets',
            summary: 'Video walkthrough of GF-safe stalls with prices and recommendations.',
          },
        ],
      },
      {
        icon: <Landmark size={24} />,
        title: 'Tower Bridge — Walk Across',
        description:
          "A short walk east from Borough Market along the Thames to London's most recognisable bridge — Victorian bascule, opened 1894. Walk over it (flat, 10 min end-to-end) for the classic Tower-of-London-and-HMS-Belfast view. Tower Hill is the best jumping-off point for the onward trip to the Expo.",
        location: 'Tower Bridge',
        time: '3:30 PM',
        image: '/images/london/tower-bridge.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'Marathon Expo at ExCeL London',
        description:
          "Collect your race bib and timing chip in person at the TCS London Marathon Running Show — the UK's largest running expo with over 100 exhibitors, product launches, and guest speakers. Open until 20:00 on Thursday. From Tower Hill, District Line → Monument → DLR direct to Custom House (about 25 min). Late-afternoon slot means the crowds have thinned. Browse gear, sample nutrition products, and soak in the race atmosphere.",
        location: 'ExCeL London, Royal Victoria Dock',
        time: '5:00 PM',
        image: '/images/london/marathon-runners.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Bubala Spitalfields',
        description:
          'Booked. A vegetarian Middle Eastern restaurant inspired by Tel Aviv\'s modern cafes. The "Bubala Knows Best" set menu (£44pp) is the dinner format Mon–Sat: sharing plates of charred hispi cabbage, confit potato latkes with toum, oyster mushroom skewers, fried aubergine with zhoug and date syrup, and hummus. The menu is almost entirely naturally gluten-free — only the bread is not, and it can be swapped for crudités. Staff are trained and knowledgeable about coeliac; they\'ll cook your dishes separately away from the bread grill on request. Not a dedicated GF kitchen, so not zero-risk, but widely trusted by coeliacs with careful ordering. Mention coeliac needs when booking (email reservations@bubala.co.uk). Easy hop back from ExCeL: DLR to Bank → Central Line → Liverpool Street, then 5 min walk.',
        location: '65 Commercial Street, Spitalfields',
        time: '7:00 PM',
        image: '/images/london/bubala.webp',
        coeliacSummary:
          'Almost entire menu is naturally gluten-free — only bread is not, and is substituted with crudités. Staff cook dishes separately away from the bread grill on request. Not a dedicated GF kitchen, so not zero-risk, but trusted by coeliac bloggers including Gluten Free Alchemist.',
        coeliacRefs: [
          {
            title: 'FindMeGlutenFree',
            url: 'https://www.findmeglutenfree.com/biz/bubala-spitalfields/6594109605806080',
            summary: 'Coeliac diners report staff cook separately from the bread grill on request.',
          },
          {
            title: 'Bubala — official',
            url: 'https://www.bubala.co.uk/spitalfields',
            summary: 'Majority of menu is GF; breads swapped for crudités. Kitchen handles gluten.',
          },
          {
            title: 'Gluten Free Alchemist',
            url: 'https://www.glutenfreealchemist.com/central-london-gluten-free-the-ultimate-coeliac-guide/',
            summary: 'Trusted coeliac blogger — praises cross-contamination understanding.',
          },
          {
            title: 'Atly',
            url: 'https://www.atly.com/gluten-free/location/Bubala',
            summary: 'Listed as Celiac friendly, low cross-contamination risk, trained staff.',
          },
        ],
      },
    ],
  },
  {
    date: 'Friday, April 24',
    dayNumber: 3,
    title: 'Embankment Run & Hop-on/Hop-off Sightseeing',
    subtitle: "Early jog along the river, then London's highlights from the top deck — easy on the legs",
    theme: 'london-taper',
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'Morning Jog — London Bridge to Holborn',
        description:
          'Out early for a 3.6km shakeout jog along the South Bank and up through the City to Holborn. Starting near London Bridge, heading west along the river past Tate Modern and the Southbank, then cutting north through the City back to the hotel. Flat, scenic, and quiet at that hour — the classic pre-race Embankment leg-loosener, a day early. Back at NYX by 8:30am.',
        location: 'Victoria Embankment',
        time: '7:45 AM',
        image: '/images/london/thames-embankment.jpg',
      },
      {
        icon: <Bus size={24} />,
        title: 'Hop-on/Hop-off Bus — Central London',
        description:
          "Sit on the top deck of a double-decker and let London come to you. The classic route covered Westminster and the Houses of Parliament, Buckingham Palace and St James's Park, Hyde Park Corner and Knightsbridge, Oxford Street, and Trafalgar Square with the National Gallery. The main sights without the miles on foot.",
        location: 'Westminster Bridge',
        time: '10:00 AM',
        image: '/images/london/sightseeing-bus.jpg',
      },
      {
        icon: <Hotel size={24} />,
        title: 'Carb Loading at the Hotel',
        description:
          'No eating out tonight — carb-loading from familiar foods back at NYX. Rice, pasta, whatever works. Hydrate. Keep it boring, keep it safe. Early evening, feet up.',
        time: 'Evening',
        image: '/images/london/nyx-hotel.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Saturday, April 25',
    dayNumber: 4,
    title: 'Taper Day — "The Play That Goes Wrong"',
    subtitle: 'Rest, a West End matinee, and final race-day prep',
    theme: 'london-taper',
    activities: [
      {
        icon: <Coffee size={24} />,
        title: 'Quick coffee near the hotel',
        description:
          'Short walk from NYX to a nearby Holborn café — nothing elaborate on race-eve. Coffee, something light, and back to base.',
        time: '9:30 AM',
        image: '/images/london/coffee.jpg',
        muted: true,
      },
      {
        icon: <Hotel size={24} />,
        title: 'Rest at the hotel',
        description:
          'Most of the morning off your feet — lay out race kit, pin the bib, charge the watch, run through the race plan. Familiar foods. Hydrate.',
        time: 'Morning',
        image: '/images/london/nyx-hotel.jpg',
        muted: true,
      },
      {
        icon: <Sparkles size={24} />,
        title: '"The Play That Goes Wrong" — Matinee',
        description:
          "An afternoon matinee of the long-running West End farce at the Duchess Theatre on Catherine Street, Covent Garden. Now in its twelfth year. The Cornley Drama Society's production of a murder mystery descends into chaos. About 2 hours including an interval. Seated the whole time, so it suits a taper day; back at the hotel by 5pm.",
        location: 'Duchess Theatre, Catherine Street',
        time: '2:30 PM',
        image: '/images/london/duchess-theatre.jpg',
      },
      {
        icon: <Sparkles size={24} />,
        title: 'Evening Prep & Visualisation',
        description:
          'Final relaxation and race prep. Visualise the course: Greenwich start, Cutty Sark at mile 6, Tower Bridge at mile 12, Canary Wharf at miles 18–19, The Mall finish. Set two alarms. From Holborn, take the Tube to Charing Cross, then Southeastern train to Blackheath — 30 minutes. Aim to arrive by 7:30am. Early bed.',
        time: 'Evening',
        image: '/images/london/london-skyline.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Sunday, April 26',
    dayNumber: 5,
    title: 'RACE DAY',
    subtitle: 'TCS London Marathon — Greenwich to The Mall',
    theme: 'london-race',
    highlight: true,
    activities: [
      {
        icon: <Footprints size={24} />,
        title: 'TCS London Marathon',
        description:
          "26.2 miles through London. Start in Blackheath/Greenwich — three colour-coded starts merge near Woolwich at mile 3. Course highlights: Cutty Sark at mile 6 (the crowd is electric), Tower Bridge at mile 12 (the noise is deafening), Canary Wharf at miles 18–19, the Embankment at mile 24, and the finish on The Mall in front of Buckingham Palace. One of the world's flattest city marathons — only 246 feet of total climb. This is what you came for.",
        location: 'Blackheath, Greenwich',
        time: 'Morning',
        image: '/images/london/marathon-runners.jpg',
      },
      {
        icon: <Sparkles size={24} />,
        title: 'Post-Race Recovery',
        description:
          "Collect your medal and finisher's bag at The Mall. Walk gently back to the hotel — about 20 minutes from the finish to Holborn via Covent Garden. Immediate recovery: stretching, rehydration, nutrition. You did it.",
        location: 'The Mall, Westminster',
        time: 'Afternoon',
        image: '/images/london/recovery.jpg',
        muted: true,
      },
      {
        icon: <Hotel size={24} />,
        title: 'Rest & Celebrate',
        description:
          'Rest and room service at the hotel. Last night at NYX Holborn — check-out is 11:00 AM tomorrow.',
        time: 'Evening',
        image: '/images/london/nyx-hotel.jpg',
        muted: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Dinner — Fish & Chips Delivery',
        description:
          'Classic British fish and chips ordered to the room. After 26.2 miles, the only appropriate evening activity is horizontal with a battered cod. Maximum carb replenishment, minimum effort.',
        time: 'Evening',
        image: '/images/london/recovery.jpg',
        muted: true,
      },
    ],
  },
  {
    date: 'Monday, April 27',
    dayNumber: 6,
    title: 'British Museum, Square Pig & Duke of York',
    subtitle: 'Check out NYX, a Holborn pub lunch & country-pub dinner with Olly & Lucy',
    theme: 'london-recovery',
    activities: [
      {
        icon: <Hotel size={24} />,
        title: 'Check Out NYX Holborn',
        description:
          "Check out of NYX Holborn by 11:00 AM. Store or carry luggage — you can drop bags at Olly & Lucy's later. The morning is yours for one last wander through the neighbourhood.",
        time: 'Morning',
        image: '/images/london/nyx-hotel.jpg',
        muted: true,
      },
      {
        icon: <Building2 size={24} />,
        title: 'British Museum — Great Court',
        description:
          "A 30-minute post-marathon wander through the Great Court — the world's largest covered public square, under a spectacular glass-and-steel roof designed by Norman Foster. The reading room at the centre is stunning. Free entry, no booking needed. Just enough culture without destroying the legs further. A 10-minute walk from the hotel.",
        location: 'Great Russell Street, Bloomsbury',
        time: '11:30 AM',
        image: '/images/london/british-museum.jpg',
      },
      {
        icon: <Beer size={24} />,
        title: 'The Square Pig and Pen — Lunch',
        description:
          'A proper Holborn local for a long recovery lunch. The Square Pig occupies a corner spot facing Red Lion Square Gardens — contemporary pub décor, high reclaimed-wood tables, and a heated outdoor terrace. Classic British pub food: burgers, pies, fish and chips. Good cask ales. The kind of pub you settle into for two or three hours on sore legs with no agenda. A 5-minute walk from the hotel.',
        location: 'Procter Street, Holborn',
        time: '12:00 PM',
        image: '/images/london/square-pig.jpg',
        coeliacSummary:
          'No dedicated GF menu — pub classics kitchen. Staff noted as willing to work with dietary requirements. Call ahead to confirm what the kitchen can accommodate for coeliac.',
        coeliacRefs: [
          {
            title: 'The Square Pig — official site',
            url: 'https://www.squarepigholborn.com/menus',
            summary:
              'No dedicated GF menu listed. Standard British pub food. Contact kitchen in advance for dietary requirements.',
          },
        ],
      },
      {
        icon: <Hotel size={24} />,
        title: "Olly & Lucy's — Arrive",
        description:
          "Northern Line from Tottenham Court Road → High Barnet (about 50 minutes). Mays Lane is a short walk from the station. Drop bags, decompress — nothing beats recovering at a friend's place.",
        location: '42 Mays Lane, Barnet',
        time: 'Afternoon',
        image: '/images/london/london-skyline.jpg',
        muted: true,
      },
      {
        icon: <Beer size={24} />,
        title: 'Dinner — The Duke of York, Potters Bar',
        description:
          'Olly drove everyone out to The Duke of York — a 1752 Grade II-listed coaching inn at Ganwick Corner on the old Great North Road, a short drive north of Barnet. A classic Brunning & Price pub: wooden floorboards, rugs, books, open fireplaces, and a secluded garden. Modern British menu that changes daily — pies, fish and chips, steaks. Coeliac UK accredited as part of the Brunning & Price group, with cross-contamination processes confirmed in place and staff trained accordingly.',
        location: 'Dancers Hill Road, Potters Bar',
        time: 'Evening',
        image: '/images/london/duke-of-york.jpg',
        coeliacSummary:
          'Coeliac UK accredited as part of the Brunning & Price pub group. Full GF menu with cross-contamination controls confirmed in place. Staff trained and aware.',
        coeliacRefs: [
          {
            title: 'The Duke of York — Coeliac Sanctuary',
            url: 'https://www.coeliacsanctuary.co.uk/wheretoeat/nationwide/brunning-and-price-pubs/the-duke-of-york',
            summary:
              'Coeliac UK accredited. Cross-contamination processes in place. Staff trained and aware of coeliac requirements.',
          },
          {
            title: 'Brunning & Price — official site',
            url: 'https://www.brunningandprice.co.uk/dukeofyork/',
            summary:
              'Coeliac UK accreditation confirmed. Modern British pub food with daily changing menu. Full GF menu available.',
          },
        ],
        coeliacUK: { url: 'https://www.coeliac.org.uk/gluten-free-accredited-venues/' },
      },
    ],
  },
  {
    date: 'Tuesday, April 28',
    dayNumber: 7,
    title: 'Barnet Morning & St Albans Day Trip',
    subtitle: "Nkora, Marley's, op shops, Hatch for lunch, the cathedral & ice cream on the way home",
    theme: 'london-explore',
    activities: [
      {
        icon: <Coffee size={24} />,
        title: 'Nkora Coffee — The Spires, High Barnet',
        description:
          'Nkora is a collective of specialty coffee shops rooted in craft and community, sourcing through Plot Roasting. The Barnet branch sits inside The Spires Shopping Centre — warm interiors, a wide selection of pastries and sandwiches, and a surprisingly large range of gluten-free treats. A strong flat white and a GF almond croissant to start the day.',
        location: 'The Spires Shopping Centre, High Barnet',
        time: '9:45 AM',
        image: '/images/london/nkora-coffee.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: "Marley's Bakery & Barnet op shops",
        description:
          "Marley's Bakery on Union Street is a neighbourhood gem run with coeliac care front of mind — the owner's mother has coeliac disease, and reviewers consistently praise the cross-contamination awareness. GF croissants, pain au chocolat, bagels with fillings, cupcakes, and brownies, all made fresh daily. After Marley's, a circuit of the Barnet High Street charity shops — classic op-shop browse.",
        location: '10 Union Street, Barnet',
        time: '11:00 AM',
        image: '/images/london/marleys-bakery.jpg',
        coeliacSummary:
          "Owner's mother is coeliac — cross-contamination awareness is a core part of how the bakery operates. Reviewers report never having a reaction. Wide range of GF products including croissants, pain au chocolat, bagels, cupcakes, and cakes. Not a dedicated GF facility but well-regarded by coeliac customers.",
        coeliacRefs: [
          {
            title: "Marley's Bakery — FindMeGlutenFree",
            url: 'https://www.findmeglutenfree.com/biz/marleys-bakery/5440530412077056',
            summary:
              "Owner's mother is coeliac. Reviewers praise cross-contamination care. GF croissants, pain au chocolat, bagels, cupcakes. Not a dedicated facility.",
          },
          {
            title: "Marley's Bakery — official site",
            url: 'https://marleysbakery.co.uk/',
            summary:
              'Handmade cakes and sweet treats, GF, dairy-free and vegan options. FREE FROM collection available online.',
          },
        ],
      },
      {
        icon: <Bus size={24} />,
        title: 'Drive to St Albans',
        description:
          "About 20 minutes north on the A1081 — through the Hertfordshire countryside into one of England's oldest cities, with Roman foundations beneath the medieval streets.",
        time: '12:00 PM',
        muted: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Hatch St Albans — Lunch',
        description:
          'Award-winning brunch and burger restaurant at Christopher Place in the heart of St Albans — one of the most popular lunch spots in the city. New York City comfort food done well: Buffalo Benedict, double cheeseburgers, waffles, and strong coffee. Staff ask whether a dietary need is an allergy or intolerance and use a separate preparation area for allergies. Trained on GF service. Ranked among the most coeliac-friendly restaurants in St Albans by multiple review platforms.',
        location: '7-8 Christopher Place, St Albans',
        time: '12:30 PM',
        image: '/images/london/hatch-stalbans.jpg',
        coeliacSummary:
          'Staff proactively ask if a dietary need is an allergy or intolerance, with separate preparation for allergies. Trained on GF service. Not a dedicated GF kitchen but well-regarded by coeliac diners. Ranked among the most coeliac-friendly in St Albans.',
        coeliacRefs: [
          {
            title: 'Hatch — FindMeGlutenFree',
            url: 'https://www.findmeglutenfree.com/biz/hatch/4976275239862272',
            summary:
              'Listed as GF-friendly. Reviewers note "great care with GF meals." Separate preparation area for allergies.',
          },
          {
            title: 'Hatch — Atly',
            url: 'https://www.atly.com/gluten-free/location/HATCH7',
            summary:
              'Trained staff, accommodating GF. Some cross-contamination risk acknowledged — not a dedicated kitchen.',
          },
          {
            title: 'Hatch — official site',
            url: 'https://www.hatchstalbans.com/',
            summary:
              'Award-winning brunch/burger restaurant in Christopher Place, St Albans. NYC comfort food with GF options.',
          },
        ],
      },
      {
        icon: <BookOpen size={24} />,
        title: 'St Albans Cathedral',
        description:
          "One of England's most ancient places of worship — the site has been in continuous religious use since around 793 AD, with the current Norman building begun in 1077 using Roman brick from the ruins of Verulamium below. The longest medieval nave in England at 85 metres. The shrine of St Alban, Britain's first Christian martyr, is still intact. Free entry, donations welcome.",
        location: 'St Albans Cathedral',
        time: '2:00 PM',
        image: '/images/london/stalbans-cathedral.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Local shops — St Albans',
        description:
          "A browse through St Peter's Street and Christopher Place — independent boutiques, Clarks, TK Maxx, and the covered market. St Albans has an unusually good high street for a city this size.",
        location: '7-8 Christopher Place, St Albans',
        time: '3:00 PM',
        muted: true,
      },
      {
        icon: <IceCream size={24} />,
        title: 'Lewis of London Ice Cream Farm — with Robin',
        description:
          "On the way back: picked up Olly's son Robin from his grandma's, then stopped at Lewis of London — a 300-acre working dairy farm at Galley Lane with an ice cream parlour on site. Over 25 flavours of real dairy ice cream made from the farm's own herd. Classic farm setting with animals, a play area, and a milk vending machine. An easy detour with a small child.",
        location: 'Galley Lane, Barnet',
        time: '4:00 PM',
        image: '/images/london/ice-cream-farm.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: "Dinner — Nando's at Olly & Lucy's",
        description:
          "Nando's takeaway at the house with O&L. PERi-PERi chicken is naturally gluten-free, and Nando's publishes a dedicated GF menu — the butterfly chicken, corn on the cob, and most grilled sides are all safe. Shared fryers for chips mean cross-contamination risk on fries, but grilled dishes are fine for coeliac.",
        time: 'Evening',
        image: '/images/london/nyx-hotel.jpg',
        muted: true,
        coeliacSummary:
          'PERi-PERi chicken is naturally GF. Dedicated GF menu available. Grilled dishes are safe for coeliac — avoid chips due to shared fryers.',
        coeliacRefs: [
          {
            title: "Nando's allergen menu",
            url: 'https://www.nandos.co.uk/food/allergen-guide',
            summary:
              'Official allergen guide. Dedicated GF menu lists safe dishes. Butterfly chicken, corn, most grilled sides are GF.',
          },
        ],
      },
    ],
  },
  {
    date: 'Wednesday, April 29',
    dayNumber: 8,
    title: 'Massage & Dublin Flight',
    subtitle: "Matt's massage at O&L's, Megan free, then LHR → Dublin 18:05",
    theme: 'london-arrival',
    activities: [
      {
        icon: <Sparkles size={24} />,
        title: "Matt — Massage at Olly & Lucy's",
        description:
          'The therapist comes to the house. 8:30 AM start, likely 60–90 min. Recovery for the legs three days post-race. Olly & Lucy are both at work today and not in the house — just you, Megan, and the therapist.',
        time: '8:30 AM',
        image: '/images/london/recovery.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'Megan — free morning near Barnet',
        description:
          "While Matt's on the table, good options nearby: Hampstead Heath (Northern Line 2 stops to Hampstead, then walk up Parliament Hill for the skyline view), Camden Market if you want bustle (Northern Line direct), or a quiet coffee on Barnet High Street. Aim to be back at the house around 11am.",
        time: '9:00 AM',
        image: '/images/london/london-skyline.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Brunch together',
        description:
          "Regroup around 11–11:30, grab something light near O&L's or in central Barnet — nothing heavy before a flight. Pack up, thank-you notes for O&L, and hit the road.",
        time: '11:30 AM',
        muted: true,
      },
      {
        icon: <Plane size={24} />,
        title: 'LHR transfer + flight to Dublin',
        description:
          'Leave Barnet ~13:00 to be safe. Options: Northern Line → Piccadilly Line to Heathrow (~1h45, cheap), or taxi (~60 min traffic-dependent, £70–90). Aim to be at T-something by 15:30 for the 18:05 flight. 1h15 flight, lands Dublin ~19:20 local. Taxi or Airlink bus to The Hoxton Dublin — 30 min. Check-in, light dinner, early night.',
        location: 'Heathrow → Dublin',
        time: '13:00 — depart',
        image: '/images/london/london-skyline.jpg',
        muted: true,
      },
    ],
  },
];

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const CoeliacUKTag: React.FC<{ accreditation: CoeliacUKAccreditation }> = ({ accreditation }) => (
  <a href={accreditation.url} target="_blank" rel="noopener noreferrer" className="coeliac-uk-tag">
    <span className="coeliac-uk-tag-badge">Coeliac UK</span>
    <span className="coeliac-uk-tag-label">Accredited{accreditation.since ? ` since ${accreditation.since}` : ''}</span>
  </a>
);

const CoeliacRefTag: React.FC<{ refs: CoeliacRef[]; summary?: string }> = ({ refs, summary }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tagRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    clearTimeout(hoverTimeout.current);
    setShowPopover(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => setShowPopover(false), 200);
  }, [isMobile]);

  const handleClick = useCallback(() => {
    if (isMobile) {
      setShowModal(true);
    }
  }, [isMobile]);

  return (
    <div className="coeliac-ref-wrapper">
      <button
        ref={tagRef}
        className="coeliac-ref-tag"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label="View coeliac references"
      >
        <ShieldCheck size={11} />
        <span>GF Reviewed</span>
      </button>

      {/* Desktop popover */}
      {showPopover && !isMobile && (
        <div
          ref={popoverRef}
          className="coeliac-popover"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="coeliac-popover-header">
            <ShieldCheck size={14} />
            <span>Coeliac References</span>
          </div>
          {summary && <p className="coeliac-summary">{summary}</p>}
          <div className="coeliac-popover-list">
            {refs.map((ref, i) => (
              <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="coeliac-ref-card">
                <span className="coeliac-ref-card-source">{ref.title}</span>
                <span className="coeliac-ref-card-summary">{ref.summary}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Mobile modal */}
      <AnimatePresence>
        {showModal && isMobile && (
          <motion.div
            className="coeliac-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="coeliac-modal"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="coeliac-modal-header">
                <ShieldCheck size={16} />
                <span>Coeliac References</span>
                <button className="coeliac-modal-close" onClick={() => setShowModal(false)}>
                  <X size={18} />
                </button>
              </div>
              {summary && <p className="coeliac-summary">{summary}</p>}
              <div className="coeliac-modal-list">
                {refs.map((ref, i) => (
                  <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="coeliac-ref-card">
                    <span className="coeliac-ref-card-source">{ref.title}</span>
                    <span className="coeliac-ref-card-summary">{ref.summary}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LondonTab: React.FC<{ resetTick?: number }> = ({ resetTick = 0 }) => {
  const [section, setSection] = useState<LondonSection>(() => hashToSection(window.location.hash));

  useEffect(() => {
    const desired = sectionToHash(section);
    if (window.location.hash !== desired) {
      window.history.replaceState(null, '', desired);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [section]);

  useEffect(() => {
    const onHashChange = () => {
      const next = hashToSection(window.location.hash);
      setSection(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- external tick; snap section back to itinerary.
    if (resetTick > 0) setSection('itinerary');
  }, [resetTick]);

  return (
    <>
      <nav className="ev-subnav" aria-label="London sections">
        <div className="ev-subnav-inner">
          {subNav.map((item) => {
            const active = section === item.id;
            return (
              <button
                key={item.id}
                className={`ev-subnav-btn ${active ? 'ev-subnav-btn--active' : ''}`}
                onClick={() => setSection(item.id)}
                aria-current={active ? 'page' : undefined}
              >
                <span className="ev-subnav-icon">{item.icon}</span>
                <span className="ev-subnav-label">
                  <span className="ev-subnav-title">{item.label}</span>
                  <span className="ev-subnav-sub">{item.sub}</span>
                </span>
                {active && (
                  <motion.span
                    layoutId="ev-subnav-underline"
                    className="ev-subnav-underline"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {section === 'fuelling' ? (
        <FuellingView race={londonRace} plan={londonFuelling} label={LONDON_LABEL} gunLabel={LONDON_GUN} />
      ) : section === 'shopping' ? (
        <ShoppingView
          race={londonRace}
          shopping={londonShopping}
          label={LONDON_LABEL}
          gunLabel={LONDON_GUN}
          storageKey="london-shopping-checked"
        />
      ) : section === 'free-stuff' ? (
        <RaceWeekView race={londonRace} raceWeek={londonRaceWeek} label={LONDON_LABEL} />
      ) : (
        <LondonItinerarySection />
      )}
    </>
  );
};

const LondonItinerarySection: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <motion.section
        className="hero london-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-background">
          <img src="/images/london/london-skyline.jpg" alt="London skyline" />
          <div className="hero-overlay london-overlay" />
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Calendar size={16} />
            <span>April 22 – 29, 2026</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            London Marathon
          </motion.h1>
          <motion.div
            className="hero-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="hero-detail">
              <MapPin size={20} />
              <span>NYX Holborn → Olly & Lucy{"'"}s → Dublin</span>
            </div>
            <div className="hero-detail">
              <Footprints size={20} />
              <span>26.2 Miles Through London</span>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="scroll-line" />
        </motion.div>
      </motion.section>

      {/* Introduction */}
      <section className="intro-section london-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>London Marathon 2026</h2>
            <p>
              Eight days in London — arrival through race day to recovery with friends. Start at NYX Hotel Holborn on
              Southampton Row for five nights — well placed for pre-race logistics: 10 min to Covent Garden, direct
              Tube to Heathrow, 30 min to the Greenwich start. After the race, decamp to Olly & Lucy{"'"}s in Barnet for
              two nights of proper recovery with friends.
            </p>
            <p>
              The marathon is Sunday April 26 — Greenwich to The Mall, one of the world{"'"}s great races. Before that:
              Southbank stroll on arrival, Victoria Park, the Natural History Museum, Borough Market and Tower Bridge on
              Thursday with the Expo at 5pm, then Westminster, Tate Modern and Greenwich on Friday, and a British Museum
              taper day on Saturday. After: AIRE Ancient Baths, The Shard, and a final massage at O&L{"'"}s before the
              18:05 flight to Dublin on Wed 29.
            </p>
            <p>
              London is expensive but the best museums are free. Expect £6–7 for a pint, £5 for a specialty coffee, and
              £15–30 for a decent dinner. The Tube is the fastest way around — get an Oyster card or use contactless.
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Itinerary Timeline */}
      <section className="timeline-section">
        <AnimatedSection>
          <h2 className="section-title london-section-title">Day by Day</h2>
        </AnimatedSection>

        <div className="timeline">
          {londonItinerary.map((day, dayIndex) => (
            <AnimatedSection key={dayIndex}>
              <div className={`day-card ${day.theme} ${day.highlight ? 'highlight london-highlight' : ''}`}>
                <div className="day-header">
                  <div className="day-number">Day {day.dayNumber}</div>
                  <div className="day-info">
                    <div className="day-date">{day.date}</div>
                    <h3 className="day-title">{day.title}</h3>
                    <p className="day-subtitle">{day.subtitle}</p>
                  </div>
                </div>

                <div className="activities">
                  {day.activities.map((activity, activityIndex) => (
                    <div
                      key={activityIndex}
                      className={`activity ${activity.muted ? 'muted' : ''} ${!activity.image ? 'no-image' : ''}`}
                    >
                      {activity.image && (
                        <div className="activity-image">
                          <img src={activity.image} alt={activity.title} />
                        </div>
                      )}
                      <div className="activity-content">
                        <div className="activity-header">
                          <div className="activity-icon london-icon">{activity.icon}</div>
                          <div className="activity-header-text">
                            <h4 className="activity-title">{activity.title}</h4>
                            {activity.time && <div className="activity-time london-time">{activity.time}</div>}
                          </div>
                        </div>
                        {activity.location && (
                          <div className="activity-location">
                            <MapPin size={12} />
                            <span>{activity.location}</span>
                          </div>
                        )}
                        <p className="activity-description">{activity.description}</p>
                        <div className="coeliac-tags-row">
                          {activity.coeliacUK && <CoeliacUKTag accreditation={activity.coeliacUK} />}
                          {activity.coeliacRefs && activity.coeliacRefs.length > 0 && (
                            <CoeliacRefTag refs={activity.coeliacRefs} summary={activity.coeliacSummary} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Interactive Map */}
      <ItineraryMap
        locations={buildMapLocations(londonItinerary, londonCoordinates)}
        center={londonCenter}
        zoom={13}
        themeColor="#e8443a"
        totalDays={10}
      />

      {/* Booking Notes */}
      <section className="intro-section london-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>Critical Booking Checklist</h2>
            <p>
              <strong>Marathon Expo</strong> — Thursday 5pm at ExCeL London. No booking needed but bring your
              confirmation email. Bib collection is mandatory and in-person only.
            </p>
            <p>
              <strong>Westminster Abbey</strong> — Pre-book online for cheaper entry and guaranteed timeslot.
            </p>
            <p>
              <strong>Indigo at One Aldwych</strong> — Book ahead. 100% GF and dairy-free — Coeliac UK accredited, zero
              cross-contamination risk.
            </p>
            <p>
              <strong>Bubala Spitalfields</strong> — Booked. Mention coeliac needs via email
              (reservations@bubala.co.uk). Dinner is set menu only Mon–Sat (£44pp).
            </p>
            <p>
              <strong>AIRE Ancient Baths</strong> — Book the 90-minute thermal bath experience online. Popular at
              weekends and after events.
            </p>
            <p>
              <strong>The Shard</strong> — Pre-book online for cheaper tickets and guaranteed entry. Late-afternoon Mon
              27 slot fits after AIRE.
            </p>
            <p>
              <strong>Los Mochis City</strong> — Book ahead for Fri 24 dinner (post-Greenwich). 100% GF, Coeliac UK
              accredited. Rooftop at 100 Liverpool Street, easy DLR from Greenwich to Bank.
            </p>
            <p>
              <strong>Mattarello</strong> — Only if O&L{"'"}s fancy heading out Tue 28 evening. Coeliac UK accredited
              with a dedicated GF kitchen.
            </p>
            <p>
              <strong>British Museum</strong> — Free, no booking. Sat 25 taper-day visit, cap at 90 min.
            </p>
            <p>
              <strong>Natural History Museum</strong> — Free, no booking. Thu 23 late-morning visit — entering via the
              Exhibition Road door skips the main-entrance queue.
            </p>
            <p>
              <strong>Massage at O&L{"'"}s</strong> — Matt, Wed 29, 8:30 AM. Confirm the therapist the day before.
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Quick Reference */}
      <section className="intro-section london-intro">
        <AnimatedSection>
          <div className="intro-content">
            <h2>Quick Reference</h2>
            <p>
              <strong>Coffee:</strong> Workshop Coffee (Fitzrovia), Ozone Coffee Roasters (Shoreditch).
            </p>
            <p>
              <strong>Restaurants:</strong> Indigo at One Aldwych (Wed 22 · 100% GF, Coeliac UK), Bubala Spitalfields
              (Thu 23 · almost entirely GF vegetarian Middle Eastern, trained staff), Los Mochis City (Fri 24 · 100% GF,
              Coeliac UK, Japanese-Mexican rooftop), Mattarello in Aldgate (Tue 28 optional · Coeliac UK, handmade GF
              pasta).
            </p>
            <p>
              <strong>Coeliac UK accredited:</strong> Indigo at One Aldwych, Los Mochis City, Mattarello — all formally
              accredited with staff trained by Coeliac UK.
            </p>
            <p>
              <strong>Other GF highlights:</strong> Borough Market (Free From Bakehouse — dedicated GF bakery, Khanom
              Krok — almost entirely GF Thai, Gujarati Rasoi — mostly GF street food).
            </p>
            <p>
              <strong>Getting around:</strong> Oyster card or contactless. Piccadilly Line from Holborn covers Heathrow,
              South Kensington, Covent Garden. Central Line for east/west. Northern Line for Camden, Bank. DLR for
              Greenwich and ExCeL. Walking is often fastest for short distances — London is very walkable.
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <motion.footer
        className="footer london-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={32} />
          <h3>See you in London</h3>
          <p>April 22 – 29, 2026 · Dublin next</p>
        </div>
      </motion.footer>
    </>
  );
};

export default LondonTab;
